import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Droplets, ArrowRight, Search, Loader2 } from "lucide-react";
import { api, goToAppStore } from "../lib/api";

const DOT = {
  soft: "bg-emerald-500",
  moderate: "bg-amber-500",
  hard: "bg-orange-500",
  very_hard: "bg-rose-600",
};

export const WaterHardnessCheck = ({ className = "" }) => {
  const [postcode, setPostcode] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const check = async (e) => {
    e?.preventDefault();
    if (!postcode.trim()) return;
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const r = await api.get("/water-hardness", { params: { postcode } });
      setResult(r.data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={"relative overflow-hidden rounded-3xl border border-stylist-gold/40 bg-white p-6 sm:p-8 shadow-2xl shadow-black/40 " + className}
      data-testid="water-hardness-check"
    >
      {/* Running-water backdrop (AI texture, gently animated) */}
      <img
        src={process.env.PUBLIC_URL + "/running-water.jpg"}
        alt=""
        aria-hidden="true"
        className="water-layer-a pointer-events-none select-none absolute inset-0 w-full h-full object-cover"
      />
      <img
        src={process.env.PUBLIC_URL + "/running-water.jpg"}
        alt=""
        aria-hidden="true"
        className="water-layer-b pointer-events-none select-none absolute inset-0 w-full h-full object-cover mix-blend-screen opacity-60"
      />
      {/* Frosted veil keeps text crisp over the moving water */}
      <div className="pointer-events-none absolute inset-0 bg-white/48 backdrop-blur-[1px]" />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-cyan-700/25 via-teal-500/12 to-cyan-900/30" />

      <div className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-stylist-gold via-stylist-rose to-stylist-gold" />
      <div className="relative z-10">
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 shrink-0 rounded-full bg-stylist-gold flex items-center justify-center shadow-md shadow-stylist-gold/30">
          <Droplets className="w-5 h-5 text-stylist-ink" />
        </div>
        <div>
          <p className="eyebrow !text-stylist-ink font-bold [text-shadow:0_1px_6px_rgba(255,255,255,0.9)]">The first fact we check</p>
          <h3 className="mt-1 font-serif text-2xl font-bold text-black leading-tight [text-shadow:0_1px_10px_rgba(255,255,255,0.95)]">
            How hard is your water?
          </h3>
          <p className="mt-1 text-sm font-semibold text-black [text-shadow:0_1px_7px_rgba(255,255,255,0.9)]">
            Your local water quietly shapes your hair. Enter your UK postcode to see what is typical for your area.
          </p>
        </div>
      </div>

      <form onSubmit={check} className="mt-5 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-stylist-muted" />
          <input
            type="text"
            value={postcode}
            onChange={(e) => setPostcode(e.target.value)}
            placeholder="e.g. SW1A 1AA"
            aria-label="UK postcode"
            data-testid="water-check-input"
            className="w-full rounded-full border border-stylist-border bg-stylist-bg pl-11 pr-4 py-3.5 text-sm text-stylist-ink placeholder:text-stylist-muted focus:outline-none focus:border-stylist-gold focus:ring-1 focus:ring-stylist-gold transition-colors"
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          data-testid="water-check-submit"
          className="group inline-flex items-center justify-center gap-2 rounded-full bg-stylist-ink text-white px-7 py-3.5 text-sm font-medium hover:bg-stylist-gold hover:text-stylist-ink transition-colors duration-300 disabled:opacity-60"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Check my water"}
        </button>
      </form>

      {error && (
        <p className="mt-3 text-sm text-rose-600" data-testid="water-check-error">{error}</p>
      )}

      <AnimatePresence>
        {result && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="mt-5 rounded-2xl bg-stylist-bg2 border border-stylist-border p-5"
            data-testid="water-check-result"
          >
            <div className="flex items-center gap-2.5" data-testid="water-check-band">
              <span className={"w-2.5 h-2.5 rounded-full " + (DOT[result.band] || "bg-amber-500")} />
              <span className="font-serif text-xl text-stylist-ink">{result.label}</span>
              <span className="text-xs font-mono uppercase tracking-widest text-stylist-muted">
                {result.postcode} · {result.area}
              </span>
            </div>
            <p className="mt-3 text-sm text-stylist-ink2 leading-relaxed">{result.description}</p>
            <p className="mt-2 text-sm text-stylist-ink leading-relaxed">
              A vetted senior stylist factors this into your diagnosis, so your routine actually
              works with the water you wash in.
            </p>
            <button
              onClick={goToAppStore}
              data-testid="water-check-cta"
              className="mt-5 group inline-flex items-center gap-2 rounded-full bg-stylist-gold text-stylist-ink px-7 py-3.5 text-sm font-medium hover:bg-stylist-ink hover:text-white transition-colors duration-300"
            >
              Start Your Diagnosis
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
      </div>
    </div>
  );
};
